// ---------------------------------------------------------------------------
// B16: Trial Banner — non-intrusive in-app upgrade prompt
// ---------------------------------------------------------------------------
import { useState } from "react";
import { Link } from "react-router";
import { useTrialStatus } from "@/hooks/use-trial";
import { Clock, Zap, X, Sparkles } from "lucide-react";
import { Skeleton } from "@/components/skeleton";

export default function TrialBanner() {
  const { trial, isLoading } = useTrialStatus();
  const [dismissed, setDismissed] = useState(false);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] px-4 py-2.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-1 items-center gap-3">
            <Skeleton className="h-4 w-4 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-3 w-40" />
            </div>
          </div>
          <Skeleton className="h-8 w-28 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!trial?.isTrialing || dismissed) return null;

  // Only show banner at certain warning levels
  if (trial.warningLevel === "none") return null;

  const isUrgent = trial.warningLevel === "urgent";
  const isWarning = trial.warningLevel === "warning";

  const bgClass = isUrgent
    ? "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5"
    : isWarning
      ? "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5"
      : "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5";

  const iconColor = isUrgent
    ? "text-[var(--color-destructive)]"
    : isWarning
      ? "text-[var(--color-warning)]"
      : "text-[var(--color-primary)]";

  return (
    <div className={`flex items-center justify-between rounded-lg border px-4 py-2.5 ${bgClass}`}>
      <div className="flex items-center gap-3">
        <Clock className={`h-4 w-4 flex-shrink-0 ${iconColor}`} />
        <div>
          <p className="text-sm text-[var(--color-foreground)]">
            {isUrgent ? (
              <><span className="font-semibold">{trial.daysRemaining} day{trial.daysRemaining !== 1 ? "s" : ""}</span> left in your {trial.trialPlan} trial</>
            ) : isWarning ? (
              <>Your trial ends in <span className="font-semibold">{trial.daysRemaining} days</span>. Keep all your features.</>
            ) : (
              <>You're on a {trial.totalDays}-day trial of <span className="font-semibold">{trial.trialPlan}</span>.</>
            )}
          </p>
          {!trial.hasPaymentMethod && isUrgent && (
            <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">
              Add a payment method to keep your account active.
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Link
          to="/settings/billing"
          className="btn-press inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium text-white"
          style={{ background: "var(--gradient-energy)" }}
        >
          {isUrgent ? (
            <><Zap className="h-3.5 w-3.5" /> Upgrade Now</>
          ) : (
            <><Sparkles className="h-3.5 w-3.5" /> See Plans</>
          )}
        </Link>
        {!isUrgent && (
          <button
            onClick={() => setDismissed(true)}
            className="rounded-md p-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
